import { ApolloClient, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { getBlockExplorerTxLink } from '@arcxmoney/arcx-react'
import { useWeb3React } from '@web3-react/core'
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import Notify, { API } from 'bnc-notify'
import { BigNumber } from 'ethers'
import React, { useEffect, useState } from 'react'
import { Network } from 'src/config'
import { useArcService } from 'src/hooks/useArcService'
import { OraclePrice, useOraclePrices } from 'src/hooks/useOraclePrices'
import { useSavings } from 'src/hooks/useSavings'
import { useSynths } from 'src/hooks/useSynths'
import { ArcService } from 'src/services/ArcService'
import { useSyntheticBalances } from 'src/services/BalanceService'
import { isNetworkSupported } from 'src/utils/isNetworkSupported'
import { BLOCK_NATIVE_API_KEY, getGraphURI } from '../constants/key'
import { Saving, Synth } from '../services/GraphService'
import { ProductQuestsService } from '../services/ProductQuestsService'

interface IArcContext {
  arc: ArcService
  synths: Synth[]
  savings: Saving[]
  getSynth: (id: string) => Synth
  getSynthByContainingName: (name: string) => Synth[]
  getSyntheticBalance: (assetAddress: string) => BigNumber
  getPrice: (oracle: string) => BigNumber
  network: Network
  notifyInstance: API
  prices: OraclePrice[]
  services: {
    productQuests: ProductQuestsService
  }
  isLoading: boolean
  setIsLoading: (loadingState: boolean) => void
  isPlaynetActivated: boolean
  setIsPlaynetActivated: (playnetActivationState: boolean) => void
  client: ApolloClient<NormalizedCacheObject>
}

const ArcContext = React.createContext<IArcContext | null>(null)

interface IProps {
  children: React.ReactNode
}

const ArcProvider = ({ children }: IProps) => {
  const context: Web3ReactContextInterface = useWeb3React()
  const [isPlaynetActivated, setIsPlaynetActivated] = useState(false)
  const [client, setClient] = useState(
    new ApolloClient({
      uri: getGraphURI(context.chainId, isPlaynetActivated),
      cache: new InMemoryCache(),
    }),
  )
  const [isLoading, setIsLoading] = useState(false)

  useEffect(
    () =>
      setClient(
        new ApolloClient({
          uri: getGraphURI(context.chainId, isPlaynetActivated),
          cache: new InMemoryCache(),
        }),
      ),
    [context.chainId, isPlaynetActivated],
  )

  const { synths, getSynth, getSynthByContainingName } = useSynths(client)

  const arc = useArcService(context, getSynthByContainingName, synths, isPlaynetActivated)

  const { savings } = useSavings(client)

  const { getSyntheticBalance } = useSyntheticBalances(client, context.account)

  const { getPrice, prices } = useOraclePrices(arc?.signer, synths)

  const [notifyInstance] = useState<API>(() =>
    Notify({
      dappId: BLOCK_NATIVE_API_KEY, // [String] The API key created by step one above
      networkId: context.chainId || 1, // [Integer] The Ethereum network ID your Dapp uses.
      darkMode: true,
      desktopPosition: 'topLeft',
    }),
  )

  useEffect(() => {
    if (!context.chainId || !context.account) {
      return
    }
    notifyInstance.config({ networkId: context.chainId })
    const { emitter } = notifyInstance.account(context.account)

    const addEtherscan = (transaction) => ({
      link: getBlockExplorerTxLink(context.chainId, transaction.hash),
    })
    emitter.on('all', addEtherscan)

    return () => notifyInstance.unsubscribe(context.account)
  }, [context.chainId, context.account, notifyInstance])

  return (
    <ArcContext.Provider
      value={{
        arc,
        getSyntheticBalance,
        getPrice,
        synths,
        savings,
        getSynth,
        getSynthByContainingName,
        isPlaynetActivated,
        setIsPlaynetActivated,
        network: isNetworkSupported(context.chainId) ? context.chainId : null,
        notifyInstance,
        prices,
        services: {
          productQuests: new ProductQuestsService(client, context.account),
        },
        client,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </ArcContext.Provider>
  )
}

export { ArcContext, ArcProvider }
